import { TableHead, TableHeaderCell, TableRow } from '@consigli/facade';
import { FC, useMemo, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { HiArrowDown, HiArrowUp } from 'react-icons/hi2';

export type SortDirection = 'asc' | 'desc';
export type SortField = 'created_at' | 'last_uploaded_package';

export interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

export interface ProjectTableHeaderProps {
  onSort?: (field: SortField, direction: SortDirection) => void;
  sortConfig: SortConfig;
  isSorting?: boolean;
}

// Helper component for sortable column arrows
export const SortArrows = memo(function SortArrows({
  isActive,
  direction,
}: {
  isActive: boolean;
  direction: SortDirection;
}) {
  if (isActive) {
    // Active column: show single arrow with dark color
    const ArrowIcon = direction === 'asc' ? HiArrowUp : HiArrowDown;
    return <ArrowIcon className="w-4 h-4 text-day-neutral-dark" />;
  }

  // Inactive column: show both arrows in gray to indicate sortable
  return (
    <>
      <HiArrowUp className="w-3 h-3 text-gray-400 hover:text-gray-600 transition-colors" />
      <HiArrowDown className="w-3 h-3 text-gray-400 hover:text-gray-600 transition-colors -mt-1" />
    </>
  );
});

export const SortableHeaderCell = memo(function SortableHeaderCell({
  field,
  label,
  sortConfig,
  onSort,
  isSorting,
}: {
  field: SortField;
  label: string;
  sortConfig: SortConfig;
  onSort?: (field: SortField, direction: SortDirection) => void;
  isSorting?: boolean;
}) {
  const isActive = sortConfig.field === field;
  const direction = isActive ? sortConfig.direction : 'desc';

  const handleClick = useCallback(() => {
    if (!onSort) return;

    if (isActive) {
      // Field is currently active, toggle direction
      const newDirection: SortDirection = sortConfig.direction === 'asc' ? 'desc' : 'asc';
      onSort(field, newDirection);
    } else {
      // Field is not active, start with default direction (desc = newest/most recent first)
      onSort(field, 'desc');
    }
  }, [field, isActive, sortConfig.direction, onSort]);

  const getTooltipText = useCallback(() => {
    const fieldDescription =
      field === 'last_uploaded_package' ? 'last activity' : label.toLowerCase();

    if (isActive) {
      const currentDirection =
        sortConfig.direction === 'asc' ? 'oldest first' : 'most recent first';
      const nextDirection = sortConfig.direction === 'asc' ? 'most recent first' : 'oldest first';
      return `Currently sorting by ${fieldDescription} (${currentDirection}). Click to switch to ${nextDirection}.`;
    } else {
      return `Click to sort by ${fieldDescription} (most recent first)`;
    }
  }, [isActive, sortConfig.direction, label, field]);

  return (
    <TableHeaderCell
      className={`pr-8 py-3 text-sm font-semibold text-day-neutral-dark cursor-pointer hover:bg-gray-50 select-none transition-colors ${
        isSorting && isActive ? 'bg-day-light-4' : ''
      }`}
      onClick={handleClick}
      title={getTooltipText()}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        <div className="flex flex-col">
          <SortArrows isActive={isActive} direction={direction} />
        </div>
      </div>
    </TableHeaderCell>
  );
});

export const ProjectTableHeader: FC<ProjectTableHeaderProps> = ({
  onSort,
  sortConfig,
  isSorting,
}) => {
  const { t } = useTranslation();

  const sortableColumns = useMemo(
    () => [
      { field: 'created_at' as SortField, label: t('project-management.created') },
      { field: 'last_uploaded_package' as SortField, label: t('project-management.last-used') },
    ],
    [t],
  );

  return (
    <TableHead className="sticky top-0 bg-day-light-4">
      <TableRow className="border-b border-gray-100">
        <TableHeaderCell className="pl-6 pr-8 py-3 text-sm font-semibold text-day-neutral-dark">
          {t('project-management.name')}
        </TableHeaderCell>
        <TableHeaderCell className="pr-8 py-3 text-sm font-semibold text-day-neutral-dark">
          {t('project-management.used-skill')}
        </TableHeaderCell>
        {sortableColumns.map(({ field, label }) => (
          <SortableHeaderCell
            key={field}
            field={field}
            label={label}
            sortConfig={sortConfig}
            onSort={onSort}
            isSorting={isSorting}
          />
        ))}
        <TableHeaderCell className="pr-6 py-3 text-sm font-semibold text-day-neutral-dark">
          {t('project-management.actions')}
        </TableHeaderCell>
      </TableRow>
    </TableHead>
  );
};
